import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchDirectory, sendChatMessage, synthesizeSpeech, SableApiError } from "../api/sableApi";
import { ParticlesOrb } from "./orb/particles-orb";
function getSRCtor() {
    const w = window;
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
function visitorKeyFor(token) {
    const key = `sable-visitor-key-${token}`;
    try {
        const existing = window.localStorage.getItem(key);
        if (existing)
            return existing;
        const fresh = crypto.randomUUID();
        window.localStorage.setItem(key, fresh);
        return fresh;
    }
    catch {
        return crypto.randomUUID();
    }
}
export default function SimpleVoiceTest() {
    const { slug } = useParams();
    const token = slug ?? "";
    const visitorKey = useRef(visitorKeyFor(token));
    const audioRef = useRef(null);
    const [log, setLog] = useState([]);
    const [listening, setListening] = useState(false);
    const [busy, setBusy] = useState(false);
    const [orbState, setOrbState] = useState("idle");
    const addLog = (line) => setLog((prev) => [...prev, `${new Date().toLocaleTimeString()}  ${line}`]);
    const getAudio = () => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
        }
        return audioRef.current;
    };
    const ask = async (text) => {
        if (!text.trim() || busy)
            return;
        setBusy(true);
        setOrbState("thinking");
        addLog(`YOU: ${text}`);
        try {
            const reply = await sendChatMessage(token, text, visitorKey.current);
            addLog(`REPLY: ${reply}`);
            const blob = await synthesizeSpeech(token, reply, visitorKey.current);
            addLog(`got audio blob, ${blob.size} bytes -- playing via plain <audio>.play(), no Web Audio graph`);
            const audio = getAudio();
            const url = URL.createObjectURL(blob);
            audio.src = url;
            audio.onended = () => {
                addLog("playback ended naturally");
                URL.revokeObjectURL(url);
                setOrbState("idle");
            };
            audio.onerror = () => {
                addLog("playback error");
                setOrbState("idle");
            };
            setOrbState("speaking");
            await audio.play();
            addLog("play() resolved -- audible now");
        }
        catch (err) {
            addLog(`ERROR: ${err instanceof SableApiError ? err.message : String(err)}`);
            setOrbState("idle");
        }
        finally {
            setBusy(false);
        }
    };
    const recognitionRef = useRef(null);
    const startListening = () => {
        // Real user gesture, right here. Directly attempt a play() on the shared element too (not a
        // dedicated silent-file priming dance, just a bare, minimal attempt) so this test can show
        // whether even a bare-minimum unlock is enough, or whether it still needs more.
        const audio = getAudio();
        audio.play().catch(() => { });
        audio.pause();
        const Ctor = getSRCtor();
        if (!Ctor) {
            addLog("SpeechRecognition not supported in this browser -- use the text box instead.");
            return;
        }
        const recognition = new Ctor();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onresult = (e) => {
            const transcript = e.results[0]?.[0]?.transcript;
            if (transcript)
                ask(transcript);
        };
        recognition.onerror = (e) => {
            addLog(`recognition error: ${e.error}`);
            setListening(false);
            setOrbState("idle");
        };
        recognition.onend = () => {
            setListening(false);
            setOrbState((s) => (s === "listening" ? "idle" : s));
        };
        recognitionRef.current = recognition;
        recognition.start();
        setListening(true);
        setOrbState("listening");
        addLog("listening...");
    };
    const stopListening = () => {
        recognitionRef.current?.stop();
        setListening(false);
        setOrbState("idle");
    };
    const [typed, setTyped] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [ownerName, setOwnerName] = useState("");
    React.useEffect(() => {
        fetchDirectory(token)
            .then((d) => {
            setOwnerName(d.ownerName ?? "Assistant");
            setLoaded(true);
            addLog(`loaded directory for ${d.ownerName}`);
        })
            .catch((err) => addLog(`directory load error: ${String(err)}`));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
    return (_jsxs("div", { style: { fontFamily: "monospace", padding: 20, maxWidth: 560, margin: "0 auto" }, children: [_jsxs("h1", { style: { fontSize: 18 }, children: ["Simple voice test -- ", loaded ? ownerName : token] }), _jsxs("p", { style: { fontSize: 13, color: "#555" }, children: ["Orb rendering added back (canvas/rAF only) -- audio is still a plain <audio>.play(), no Web Audio graph, no fades, no filler clips. Orb state: ", _jsx("b", { children: orbState })] }), _jsx("div", { style: { width: 160, height: 160, margin: "0 auto 12px" }, children: _jsx(ParticlesOrb, { state: orbState, size: 160, colorFrom: "#2f6f7d", colorTo: "#5fb3c7", label: "test orb" }) }), _jsxs("div", { style: { display: "flex", gap: 8, margin: "12px 0" }, children: [_jsx("button", { onClick: listening ? stopListening : startListening, disabled: busy, children: listening ? "Stop listening" : "Start listening" }), _jsx("input", { type: "text", value: typed, onChange: (e) => setTyped(e.target.value), placeholder: "or type a question", style: { flex: 1 } }), _jsx("button", { onClick: () => {
                            const t = typed;
                            setTyped("");
                            ask(t);
                        }, disabled: busy || !typed.trim(), children: "Send" })] }), _jsx("div", { style: { background: "#111", color: "#0f0", padding: 10, fontSize: 12, height: 320, overflowY: "auto", whiteSpace: "pre-wrap" }, children: log.join("\n") })] }));
}
